import React from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { FiRotateCcw, FiSearch, FiUser } from "react-icons/fi";
import { ClearButton } from "../../common/ClearControls";
import DashboardFilterSelect from "./DashboardFilterSelect";
import styles from "./Dashboard.module.scss";

type Props = {
  patientQuery: string;
  clinician: string;
  patients: string[];
  clinicians: Array<{ value: string; label: string }>;
  loading: boolean;
  onPatientChange: (query: string) => void;
  onClinicianChange: (clinician: string) => void;
};

export default function TranscriptionFilters({ patientQuery, clinician, patients, clinicians, loading, onPatientChange, onClinicianChange }: Props) {
  const active = Boolean(patientQuery.trim()) || clinician !== "all";
  const clearAll = () => { onPatientChange(""); onClinicianChange("all"); };

  return <section className={`${styles.filters} ${styles.transcriptionFilters}`} aria-label="Transcription filters">
    <div className={styles.filtersRow}>
      <div className={`${styles.filter} ${styles.patientSearchFilter}`}>
        <label htmlFor="transcription-patient-search">Search patients</label>
        <div className={styles.filterInputWrapper} data-active={Boolean(patientQuery.trim())}>
          <Autocomplete
            id="transcription-patient-search"
            freeSolo
            options={patients}
            value={patientQuery || null}
            inputValue={patientQuery}
            onChange={(_, value) => onPatientChange(value || "")}
            onInputChange={(_, value, reason) => { if (reason === "input" || reason === "clear") onPatientChange(value); }}
            forcePopupIcon={false}
            clearText="Clear patient search"
            clearOnBlur={false}
            autoHighlight
            openOnFocus
            loading={loading}
            loadingText="Loading recordings..."
            componentsProps={{ paper: { className: `${styles.patientSearchPaper} ${styles.aiFilterMenu}` } }}
            renderOption={(props, patient) => <li {...props} key={patient}>
              <span className={styles.patientSearchAvatar} aria-hidden="true"><FiUser /></span>
              <div className={styles.patientSearchDetails}><strong>{patient}</strong></div>
            </li>}
            renderInput={(params) => <TextField
              {...params}
              placeholder="Search by patient"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: <FiSearch className={styles.patientSearchIcon} aria-hidden="true" />,
                endAdornment: <>{loading && <CircularProgress size={15} className={styles.patientSearchSpinner} color="inherit" aria-label="Loading recordings" />}{params.InputProps.endAdornment}</>,
                style: { height: "40px", paddingLeft: "12px", paddingTop: 0, paddingBottom: 0, fontWeight: 500 },
              }}
              sx={{ "& .MuiOutlinedInput-root fieldset": { border: "none" }, "& input::placeholder": { color: "#786588", opacity: 1 } }}
            />}
          />
        </div>
      </div>
      <div className={`${styles.filter} ${styles.clinicianFilter}`}>
        <label id="transcription-clinician-filter-label" htmlFor="transcription-clinician-filter">Clinician</label>
        <DashboardFilterSelect
          id="transcription-clinician-filter"
          label="Clinician"
          tone="purple"
          value={clinician}
          options={[{ value: "all", label: "All clinicians" }, ...clinicians]}
          fallbackLabel="Selected clinician"
          onChange={onClinicianChange}
          onClear={() => onClinicianChange("all")}
        />
      </div>
    </div>
    {active && <div className={styles.filtersFooter}>
      <div className={styles.activeFilters} role="group" aria-label="Active transcription filters">
        {patientQuery.trim() && <span className={styles.activeFilterPill}>
          <FiUser aria-hidden="true" /><span>{patientQuery.trim()}</span>
          <ClearButton label="Remove patient filter" onClear={() => onPatientChange("")} />
        </span>}
        {clinician !== "all" && <span className={styles.activeFilterPill}>
          <span>Clinician: {clinicians.find((option) => option.value === clinician)?.label || "Selected clinician"}</span>
          <ClearButton label="Remove clinician filter" onClear={() => onClinicianChange("all")} />
        </span>}
      </div>
      <button type="button" className={styles.clearFiltersButton} onClick={clearAll} aria-label="Clear all filters"><FiRotateCcw aria-hidden="true" />Clear all</button>
    </div>}
  </section>;
}
